import { useEffect, useMemo, useState } from "react";

import { C, font } from "../styles/tokens";
import { InlineIcon } from "./InlineIcon";

// =============================================================================
// Shared client-side pagination for every table / long list in the app.
// =============================================================================

export const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

export function usePagination(items, initialPageSize = 10) {
  const [page, setPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(initialPageSize);

  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Clamp page index when list shrinks
  useEffect(() => {
    if (page > totalPages - 1) setPage(totalPages - 1);
  }, [page, totalPages]);

  const safePage = Math.min(page, totalPages - 1);
  const pageItems = useMemo(
    () => items.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [items, safePage, pageSize],
  );

  return {
    page: safePage,
    setPage,
    pageSize,
    setPageSize: (n) => {
      setPageSize(n);
      setPage(0);
    },
    total,
    totalPages,
    pageItems,
    reset: () => setPage(0),
  };
}

/** Page numbers to render */
function pageList(current, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  const pages = new Set([0, totalPages - 1, current - 1, current, current + 1]);
  const sorted = [...pages]
    .filter((p) => p >= 0 && p < totalPages)
    .sort((a, b) => a - b);
  const out = [];
  let prev = -1;
  for (const p of sorted) {
    if (prev !== -1 && p - prev > 1) out.push(null);
    out.push(p);
    prev = p;
  }
  return out;
}

/**
 * Footer PaginationBar component
 */
export function PaginationBar({
  pager,
  itemLabel = "entries",
  style,
  alwaysShow = true,
}) {
  const { page, setPage, pageSize, setPageSize, total, totalPages } = pager;
  if (total === 0 && !alwaysShow) return null;

  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        padding: "14px 20px",
        borderTop: "1px solid #e2e8f0",
        background: "#f8fafc",
        fontFamily: font,
        borderRadius: "0 0 16px 16px",
        ...style,
      }}
    >
      <span style={{ fontSize: 13, color: "#64748b" }}>
        Showing{" "}
        <strong style={{ color: "#0f172a" }}>
          {from}-{to}
        </strong>{" "}
        of <strong style={{ color: "#0f172a" }}>{total}</strong> {itemLabel}
      </span>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 14,
        }}
      >
        <label
          style={{
            fontSize: 12,
            color: "#64748b",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>Per page</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            style={{
              fontSize: 12,
              fontFamily: font,
              color: "#0f172a",
              padding: "4px 8px",
              borderRadius: 6,
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              cursor: "pointer",
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>

        <nav
          aria-label="Pagination"
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <PagerButton
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
            ariaLabel="Previous page"
          >
            <InlineIcon name="chevronLeft" />
            Previous
          </PagerButton>

          {pageList(page, totalPages).map((p, i) =>
            p === null ? (
              <span
                key={`gap-${i}`}
                style={{ fontSize: 12, color: "#94a3b8", padding: "0 2px" }}
              >
                ...
              </span>
            ) : (
              <PagerButton
                key={p}
                active={p === page}
                onClick={() => setPage(p)}
                ariaLabel={`Page ${p + 1}`}
              >
                {p + 1}
              </PagerButton>
            ),
          )}

          <PagerButton
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
            ariaLabel="Next page"
          >
            Next
            <InlineIcon name="chevronRight" />
          </PagerButton>
        </nav>
      </div>
    </div>
  );
}

function PagerButton({ children, onClick, disabled, active, ariaLabel }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={active ? "page" : undefined}
      style={{
        minWidth: 32,
        height: 32,
        padding: "0 10px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        fontSize: 13,
        fontWeight: active ? 700 : 600,
        fontFamily: font,
        color: active ? "#ffffff" : disabled ? "#cbd5e1" : "#334155",
        background: active ? "#2563eb" : disabled ? "#f8fafc" : "#ffffff",
        border: `1px solid ${active ? "#2563eb" : "#cbd5e1"}`,
        borderRadius: 8,
        cursor: disabled || active ? "default" : "pointer",
        transition: "all 0.15s ease",
      }}
    >
      {children}
    </button>
  );
}
